import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PulseDot, PulseWord } from "@/components/pulse/PulseDot";
import { SummaryPanel } from "@/components/pulse/SummaryPanel";
import { PulseMath } from "@/components/pulse/PulseMath";
import { PulseActions } from "@/components/pulse/PulseActions";
import { bandOf, movementLine, fmtTime } from "@/components/pulse/pulse";

// The community manager's Pulse card (Overview). Own community only; no tuning, no other
// communities. Never rendered for board members (the API 403s them too).
export function PulseCard({ tenantId, tenant, role }) {
  const on = !!(tenant?.pulse_entitled && tenant?.pulse_enabled) || !!(tenant?.entitlements?.pulse && tenant?.pulse_enabled);
  const [data, setData] = useCachedState(`pulse-card:${tenantId}`, null);
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState(false);
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem("askredline.pulseCardCollapsed") === "true"; } catch { return false; } });
  const toggle = () => setCollapsed((c) => { try { localStorage.setItem("askredline.pulseCardCollapsed", String(!c)); } catch {} return !c; });
  const load = useCallback(() => api.get(`/tenants/${tenantId}/community-pulse`).then((r) => setData(r.data)).catch(() => setData(null)), [tenantId, setData]);
  useEffect(() => { if (on && role !== "board_member") load(); }, [on, role, load]);
  if (!on || role === "board_member" || !data?.on) return null;
  const row = { pulse_on: true, snapshot: data.snapshot, name: data.name };
  const band = bandOf(row);
  const s = data.snapshot;
  const scored = band !== "quiet";
  const toAct = (data.act || []).filter((r) => r.count > 0).length;
  return (
    <section data-testid="pulse-card" className="rounded-2xl border border-[#E8E2D9] bg-white overflow-hidden">
      <div className="grid grid-cols-[20px_1fr] sm:grid-cols-[24px_1fr_auto] gap-3 sm:gap-4 items-center px-4 sm:px-5 py-4">
        <PulseDot band={band} quiet={!!s?.expected} size={14} testid="pulse-card-dot" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading font-bold text-[#1C1917] text-sm">Pulse</span>
            <PulseWord band={band} testid="pulse-card-word" />
            {scored && <span className="font-mono text-sm font-extrabold text-[#1C1917]" data-testid="pulse-card-score">{s?.score}</span>}
            {s?.falling && <span className="text-[10px] font-extrabold text-[#B91C1C] uppercase tracking-wide">falling</span>}
            {s?.expected && <span className="text-[10px] font-bold text-[#8C827A] uppercase tracking-wide" data-testid="pulse-card-expected">· expected</span>}
          </div>
          <div className="text-xs font-bold text-[#8C827A] mt-0.5" data-testid="pulse-card-move">
            {movementLine(row)}{s?.computed_at ? ` · updated ${fmtTime(s.computed_at)}` : ""}
          </div>
        </div>
        <div className="col-start-2 sm:col-start-auto flex flex-wrap items-center gap-2">
          <Link to="/dashboard/pulse" data-testid="pulse-card-open" className="text-xs font-bold text-[#57534E] hover:text-[#1C1917]">Full view →</Link>
          {scored && (
            <button type="button" data-testid="pulse-card-why" onClick={() => setWhy(true)} className="text-xs font-bold text-[#57534E] hover:text-[#1C1917]">Why {s?.score}?</button>
          )}
          {scored && (
            <button type="button" data-testid="pulse-card-summary-btn" onClick={() => setOpen((o) => !o)}
              className={`rounded-full px-3 py-1.5 text-xs font-bold border ${open ? "bg-ink text-white border-ink" : "bg-white border-[#E8E2D9] hover:border-ink"}`}>Summary</button>
          )}
          {toAct > 0 && (
            <button type="button" data-testid="pulse-card-toggle" onClick={toggle} aria-expanded={!collapsed} aria-label={collapsed ? "Show what to act on" : "Hide what to act on"}
              className="inline-flex items-center gap-1 rounded-full border border-[#E8E2D9] bg-white pl-2.5 pr-1.5 py-1 text-xs font-bold text-[#57534E] hover:border-ink hover:text-[#1C1917]">
              {collapsed && <span data-testid="pulse-card-toact">{toAct} to act on</span>}
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${collapsed ? "" : "rotate-180"}`} strokeWidth={2.2} />
            </button>
          )}
        </div>
      </div>
      {open && scored && (
        <div className="px-4 pb-4 -mt-1">
          <SummaryPanel tenantId={tenantId} name={data.name} snapshot={s} canExpected onWhy={() => setWhy(true)} onExpectedChange={load} />
        </div>
      )}
      {!collapsed && toAct > 0 && <PulseActions tenantId={tenantId} name={data.name} rows={data.act} compact limit={3} />}
      <Dialog open={why} onOpenChange={setWhy}>
        <DialogContent className="max-w-3xl" data-testid="pulse-card-math-dialog">
          <DialogHeader><DialogTitle className="font-heading">Why {s?.score}? The math</DialogTitle></DialogHeader>
          <p className="text-sm text-[#57534E] -mt-1">Pulse starts at 100. Each signal has a healthy line and a trouble line and a number of points at stake — a signal at or better than healthy costs nothing, one at or worse than trouble costs all of its points, in between it costs a share. Pulse is what's left.</p>
          <PulseMath math={data.math} name={data.name} tenantId={tenantId} />
        </DialogContent>
      </Dialog>
    </section>
  );
}
